import { Color } from "../../../../utils/sys/Color";
import { Dictionary } from "../../../../utils/sys/Dictionary";
import Geom2d, { Point2d } from "../../../../common/geometry/Geom2d";
import TabletopCanvasMouseEvent from "../events/TabletopCanvasMouseEvent";
import TabletopCanvasNavigationController from "./TabletopCanvasNavigationController";
import TabletopGrid from "../../objects/grid/TabletopGrid";
import TabletopRulerElement from "../../objects/element/TabletopRulerElement";
import TabletopToken from "../../objects/token/TabletopToken";
import TabletopUniverse from "../../objects/TabletopUniverse";
import TabletopViewport from "../../objects/TabletopViewport";
import TabletopCanvasKeyEvent from "../events/TabletopCanvasKeyEvent";

export default class TabletopCanvasManipulateTokenController extends TabletopCanvasNavigationController {
    private _candidateToken: TabletopToken | null = null;
    private _targetToken: TabletopToken | null = null;
    private _targetRulerElement: TabletopRulerElement | null = null;
    private _cellHighlights: Dictionary<
        number,
        Dictionary<number, Color>
    > | null = null;

    public constructor(viewport: TabletopViewport, universe: TabletopUniverse) {
        super(viewport, universe);
    }

    public override dispose(): void {
        this.setCellHighlights(null);

        if (this._candidateToken != null) {
            this._candidateToken.highlighted = false;
            this._candidateToken = null;
        }

        if (this._targetToken != null) {
            this._targetToken.selected = false;
            this._targetToken = null;
        }

        if (this._targetRulerElement != null) {
            this._universe.removeElement(this._targetRulerElement);
            this._targetRulerElement = null;
        }

        super.dispose();
    }

    private selectTokenAtPoint(point: Point2d): TabletopToken | null {
        const candidates = this._universe.tokens.filter((token) =>
            token.containsPoint(point),
        );

        if (candidates.length === 1) {
            return candidates[0];
        } else if (candidates.length > 1) {
            const distances = candidates.map((c) =>
                Geom2d.distanceBetween(c.x, c.y, point.x, point.y),
            );
            const shortest = Math.min(...distances);
            const index = distances.findIndex((d) => d === shortest);

            return index >= 0 ? candidates[index] : null;
        }

        return null;
    }

    private setTargetToken(token: TabletopToken | null): void {
        if (this._targetToken != null) {
            this._targetToken.selected = false;
        }
        if (token == null) {
            if (this._targetRulerElement != null) {
                this._universe.removeElement(this._targetRulerElement);
                this._targetRulerElement = null;
            }
            if (this._cellHighlights != null) {
                this.setCellHighlights(null);
            }
        }

        this._targetToken = token;
        if (token != null) {
            token.selected = true;
        }
    }

    private setCellHighlights(
        cellHighlights: Dictionary<number, Dictionary<number, Color>> | null,
    ): void {
        const grid = this._universe.gridElement;

        if (grid != null) {
            const existing = this._cellHighlights;
            if (existing != null) {
                existing.keys.forEach((x) => {
                    existing.get(x)?.keys.forEach((y) => {
                        grid.setCellHighlight(x, y, null);
                    });
                });
            }

            if (cellHighlights != null) {
                cellHighlights.keys.forEach((x) => {
                    cellHighlights.get(x)?.keys.forEach((y) => {
                        grid.setCellHighlight(
                            x,
                            y,
                            cellHighlights.get(x)?.get(y) ?? null,
                        );
                    });
                });
            }
        }

        this._cellHighlights = cellHighlights;
    }

    public override onCanvasMouseMove(e: TabletopCanvasMouseEvent): void {
        super.onCanvasMouseMove(e);

        const candidate = this.selectTokenAtPoint(e.universePosition);

        if (candidate !== this._candidateToken) {
            if (this._candidateToken != null) {
                this._candidateToken.highlighted = false;
            }

            if (candidate != null) {
                this._candidateToken = candidate;
                candidate.highlighted = true;
            }

            this._candidateToken = candidate;
        }

        if (this._targetToken != null) {
            const cellCoord =
                this._universe.gridElement?.getCellCoordinatesFromUniversePoint(
                    e.universePosition,
                );

            const tokenExists =
                cellCoord == null
                    ? false
                    : this._universe.getTokensAtGridCoordinate(cellCoord)
                          .length > 0;

            this.setCellHighlights(
                cellCoord == null
                    ? new Dictionary()
                    : new Dictionary<number, Dictionary<number, Color>>([
                          {
                              key: cellCoord?.x,
                              value: new Dictionary<number, Color>([
                                  {
                                      key: cellCoord?.y,
                                      value: tokenExists
                                          ? TabletopGrid.hightlightColorInvalid
                                          : TabletopGrid.hightlightColorValid,
                                  },
                              ]),
                          },
                      ]),
            );
        }

        if (this._targetRulerElement != null) {
            this._targetRulerElement.setLastPoint(
                e.ctrlKey
                    ? (this._universe.gridElement?.snapUniversePointToGrid(
                          e.universePosition,
                      ) ?? e.universePosition)
                    : e.universePosition,
            );
        }
    }

    public override onCanvasMouseDrag(e: TabletopCanvasMouseEvent): void {
        if (e.altKey) {
            super.onCanvasMouseDrag(e);
            return;
        }
    }

    public override onCanvasMouseDown(e: TabletopCanvasMouseEvent): void {
        if (e.altKey) {
            super.onCanvasMouseDown(e);
            return;
        }

        if (this._targetToken == null) {
            const candidate =
                this._candidateToken ??
                this.selectTokenAtPoint(e.universePosition);

            if (candidate == null) {
                return;
            }

            this._targetRulerElement = new TabletopRulerElement({
                points: [candidate.position, e.universePosition],
                showDistance: true,
            });
            this._universe.addElement(this._targetRulerElement);

            this.setTargetToken(candidate);
        } else {
            const candidate =
                this._candidateToken ??
                this.selectTokenAtPoint(e.universePosition);

            if (candidate == null) {
                const position =
                    this._universe.gridElement?.snapUniversePointToGrid(
                        e.universePosition,
                    ) ?? e.universePosition;
                const gridCoordinate =
                    this._universe.gridElement?.getCellCoordinatesFromUniversePoint(
                        position,
                    );

                const tokenExists =
                    gridCoordinate == null
                        ? false
                        : this._universe.getTokensAtGridCoordinate(
                              gridCoordinate,
                          ).length > 0;

                if (!tokenExists) {
                    this._targetToken.position = position;

                    if (this._targetRulerElement != null) {
                        this._targetRulerElement.setPointAt(0, position);
                    }
                }
            } else if (candidate === this._targetToken) {
                this.setTargetToken(null);

                if (this._targetRulerElement != null) {
                    this._universe.removeElement(this._targetRulerElement);
                    this._targetRulerElement = null;
                }
            }
        }
    }

    public override onCanvasMouseUp(e: TabletopCanvasMouseEvent): void {}

    public override onCanvasKeyDown(e: TabletopCanvasKeyEvent): void {
        if (e.key === "Escape") {
            if (this._targetToken != null) {
                this.setTargetToken(null);
            }
        }
    }
}
